import React from 'react';
import { createPortal } from 'react-dom';
import { FaTimes } from 'react-icons/fa';

interface DialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: React.ReactNode;
  disableOverlayClick?: boolean;
}

export function Dialog({ open, onOpenChange, children, disableOverlayClick = false }: DialogProps) {
  const [mounted, setMounted] = React.useState(false);

  React.useEffect(() => {
    setMounted(true);
  }, []);

  if (!mounted || !open) return null;

  return createPortal(
    <div
      onClick={() => !disableOverlayClick && onOpenChange(false)}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 99999,
        backgroundColor: 'rgba(0,0,0,0.5)',
        backdropFilter: 'blur(2px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '16px',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ position: 'relative', display: 'flex', flexDirection: 'column' }}
      >
        {children}
      </div>
    </div>,
    document.body
  );
}

export function DialogContent({ 
  children, 
  className = '', 
  style, 
  onClose,
  showClose = true
}: { 
  children: React.ReactNode; 
  className?: string; 
  style?: React.CSSProperties, 
  onClose?: (s: boolean) => void;
  showClose?: boolean;
}) {
  return (
    <div
      className={`relative p-8 bg-white rounded-[16px] shadow-xl w-full max-h-[90vh] overflow-y-auto ${className}`}
      style={{ maxWidth: 'min(900px, 95vw)', ...style }}
    >
      {onClose && showClose &&
        <DialogClose onClick={() => onClose?.(false)} />
      }

      {children}
    </div>
  );
}

export function DialogHeader({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return <div className={`flex flex-col space-y-1.5 text-center sm:text-left mb-6 ${className}`}>{children}</div>;
}

export function DialogTitle({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return <h2 className={`text-2xl font-bold tracking-tight text-gray-900 ${className}`}>{children}</h2>;
}

export function DialogClose({ onClick }: { onClick?: () => void }) {
  return (
    <button
      onClick={onClick}
      style={{ right: '24px', top: '24px' }}
      className="absolute right-[24px] rounded-full opacity-70 transition-opacity hover:opacity-100 focus:outline-none"
    >
      <FaTimes size={18} className="text-text-primary font-medium" />
      <span className="sr-only">Close</span>
    </button>
  );
}
